/**
 * Phase 45 Plan 45-05 — Portfolio group rollup row.
 * Phase 50 Plan 50-08 — Extended with optional tier color chip.
 *
 * Renders between groups when `groupBy !== 'none'` per UI-SPEC §Surface 1
 * "Group rollup row":
 *   bg: ms-surface-2/60 with border-y border-ms-border-subtle
 *   spans columns 1-4 with aggregated values (sum hoursLogged, sum revenue,
 *   mean burnPct). Columns 6-7 stay empty.
 *
 * MKS-328: column 5 carries the group's revenue-weighted margin (it was
 * blank, so a group showed revenue with no indication of what it earned).
 * `margin` is null — rendered "—" — when the group has no positive revenue.
 *
 * When `tierOrderIndex` is provided (non-null number), a small colored dot is
 * shown before the label — matching Phase 49 tier badge palette via getTierColor.
 * The dot is omitted for the "No tier" group (tierOrderIndex === null) and for
 * non-tier group-by modes (tierOrderIndex === undefined).
 */
import { BurnBar } from './BurnBar';
import { formatMargin, formatPortfolioMoney, marginToneClass } from './money';
import { portfolioGridClass } from './portfolioGrid';
import { getTierColor } from '../../utils/format';

export interface PortfolioGroupRowProps {
  label: string;
  projectCount: number;
  totalHoursLogged: number;
  totalRevenue: number;
  meanBurnPct: number;
  billable: boolean;
  /**
   * MKS-328 — the group's revenue-weighted margin, or null when the group
   * has no positive revenue to weigh against.
   */
  margin: number | null;
  /**
   * MKS-332 — false when the API omitted cost and margin for this viewer.
   * Decided by PortfolioTable and passed down so this row's cell count
   * matches the header's grid.
   */
  costVisible?: boolean;
  /**
   * Set to a `number` for tier groups with a real orderIndex (chip shown).
   * Set to `null` for the "No tier" group (no chip).
   * Omit (or `undefined`) for customer/owner groups (no chip).
   */
  tierOrderIndex?: number | null;
}

function formatRevenue(value: number, billable: boolean): string {
  if (!billable) return '—';
  if (value === 0) return '0 Kč';
  return formatPortfolioMoney(value);
}

export function PortfolioGroupRow({
  label,
  projectCount,
  totalHoursLogged,
  totalRevenue,
  meanBurnPct,
  billable,
  margin,
  costVisible = true,
  tierOrderIndex,
}: PortfolioGroupRowProps) {
  const hours = `${Math.round(totalHoursLogged)}h`;

  return (
    <div
      className={`${portfolioGridClass(costVisible)} items-center bg-ms-surface-2/60 border-y border-ms-border-subtle`}
      role="row"
    >
      {/* Col 1 — group label + count */}
      <div className="px-4 py-2 min-w-0">
        <div className="flex items-center gap-1.5 text-[11.5px] font-semibold text-ms-text-strong truncate">
          {/* Tier color chip — only rendered when tierOrderIndex is a number (not null/undefined) */}
          {typeof tierOrderIndex === 'number' && (
            <span
              className="inline-block w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: getTierColor(tierOrderIndex) }}
              aria-hidden="true"
            />
          )}
          {label}{' '}
          <span className="text-[10.5px] font-normal text-ms-text-muted tabular-nums">
            · {projectCount} project{projectCount === 1 ? '' : 's'}
            {/* MKS-328 — the hours total lives here because the Margin column
                claims its own cell below, so no figure sits under a header
                that does not describe it. */}
            {` · ${hours}`}
          </span>
        </div>
      </div>

      {/* Col 2 — empty */}
      <div className="px-2 py-2" />

      {/* Col 3 — compact burn bar (height 4) */}
      <div className="px-2 py-2">
        <BurnBar pct={meanBurnPct} height={4} />
      </div>

      {/* Col 4 — total revenue */}
      <div className="px-2 py-2 text-right text-[11.5px] tabular-nums font-medium text-ms-text-strong">
        {formatRevenue(totalRevenue, billable)}
      </div>

      {/* Col 5 — group margin (MKS-328); absent entirely without cost access
          (MKS-332). */}
      {costVisible && (
        <div
          className={`px-2 py-2 text-right text-[11.5px] tabular-nums font-medium ${marginToneClass(
            margin,
          )}`}
        >
          {formatMargin(margin)}
        </div>
      )}

      {/* Col 6-7 — empty */}
      <div className="px-2 py-2" />
      <div className="px-4 py-2" />
    </div>
  );
}
